import { useEffect, useState } from "react";
import "./AppPTORequests.css";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "../../../ds2/components";
import { selectUIStates, selectUser, setUIStates } from "../../../redux/features/appData";
import { EmployeeRecord, TimeOffRequest, TimeOffRequestExtended, TimeOffRequestStatus, TimeOffRequestStatusLabels } from "winslow-core";
import { apiGetTimeOffRequestsForWorkspace, apiUpdateTimeOffRequestStatus } from "../../../services/TimeOffRequestService";
import { Check, RefreshCw, X, FileText } from "lucide-react";
import { Icon } from "../../../ds2/components";
import { erGetCPValue } from "../app-employee-records/employeeRecordUtils";
import { apiGetEmployeeRecord } from "../../../services/EmployeeRecordService";
import { PTOPolicyTypeDescription } from "./AppPTOPolicyEditor";
import { ValidatedImage } from "../../common/ValidatedImage";
import { getDefaultUserAvatar } from "winslow-core";
import "../../../ds2/components/Table.css";

type AppPTORequestsProps = {
    dynamicContext?: {
        employeeRecordIds?: string[]; // Optional context for employee records
    };
    disabled?: boolean;
}

export const AppPTORequests = ({dynamicContext, disabled = false}: AppPTORequestsProps) => {
    const dispatch = useDispatch();
    const storeUser = useSelector(selectUser);
    const uiStates = useSelector(selectUIStates);
   
    const [requests, setRequests] = useState<TimeOffRequestExtended[] | undefined>(undefined);
    const [employeeRecord, setEmployeeRecord] = useState<EmployeeRecord | undefined>(undefined);


    useEffect(() => {
        dispatch(setUIStates({...uiStates, hrisLocation: "Time Off Requests"}));
    }, []);

    useEffect(() => {
        if (!storeUser?.currentWorkspaceId) {
            return;
        }

        if (disabled) {
            return;
        }

        // Fetch time off requests from the server
        (async () => {
            const timeOffRequests = await apiGetTimeOffRequestsForWorkspace(storeUser.currentWorkspaceId || "");

            console.log("[AppPTORequests] timeOffRequests", timeOffRequests);
            if (timeOffRequests) {

                if (dynamicContext?.employeeRecordIds && 
                    dynamicContext?.employeeRecordIds.length > 0 && 
                    dynamicContext?.employeeRecordIds[0]) {

                    const matchEmployeeRecordId = dynamicContext?.employeeRecordIds[0];

                    // Filter requests by employee record IDs if provided
                    const filteredRequests = timeOffRequests.filter(request => request.employeeRecordId === matchEmployeeRecordId);
                    setRequests(filteredRequests);
                } else {
                    setRequests(timeOffRequests);
                }
            }
        })();
    }, [storeUser?.currentWorkspaceId]);

    useEffect(() => {
        (async () => {
            if (dynamicContext?.employeeRecordIds && dynamicContext?.employeeRecordIds.length > 0 && dynamicContext?.employeeRecordIds[0]) {
                const employeeRecord = await apiGetEmployeeRecord(dynamicContext?.employeeRecordIds[0] || "");
                if (employeeRecord) {
                    setEmployeeRecord(employeeRecord);
                }
            }
        })();
    }, [dynamicContext?.employeeRecordIds]);

    const handleApprove = async (requestId: string) => {
        const updatedRequest = await apiUpdateTimeOffRequestStatus(requestId, TimeOffRequestStatus.APPROVED);
        if (updatedRequest && requests) {
            setRequests(requests.map(r => r.id === requestId ? updatedRequest : r));
        }
    };

    const handleReject = async (requestId: string) => {
        const updatedRequest = await apiUpdateTimeOffRequestStatus(requestId, TimeOffRequestStatus.REJECTED);
        if (updatedRequest && requests) {
            setRequests(requests.map(r => r.id === requestId ? updatedRequest : r));
        }
    };

    const handleRefresh = async () => {
        if (dynamicContext?.employeeRecordIds && dynamicContext?.employeeRecordIds.length > 0 && dynamicContext?.employeeRecordIds[0]) {
            const employeeRecord = await apiGetEmployeeRecord(dynamicContext?.employeeRecordIds[0] || "");
            if (employeeRecord) {
                setEmployeeRecord(employeeRecord);
            }
        }
    };

    const getInitials = (employeeRecord: EmployeeRecord) => {
        return `${erGetCPValue(employeeRecord, "system-firstName")?.charAt(0) || ''}${erGetCPValue(employeeRecord, "system-lastName")?.charAt(0) || ''}`;
    }

    const getFullName = (employeeRecord: EmployeeRecord) => {
        return `${erGetCPValue(employeeRecord, "system-firstName")} ${erGetCPValue(employeeRecord, "system-lastName")}`;
    }



    return (
        <div className="app-pto-requests">
            <div className="app-pto-requests-header dc-component-header">
                <div className="app-pto-requests-header-title">
                    {employeeRecord && employeeRecord.id !== storeUser?.employeeRecordId && 
                        <div className="app-pto-requests-header-title-employee">
                            {"For "}
                            <div className="dc-component-avatar">
                                {employeeRecord.avatarURL ? (
                                    <img src={employeeRecord.avatarURL} />
                                ) : (
                                    <div className="app-pto-requests-header-title-avatar-placeholder">
                                        {getInitials(employeeRecord)}
                                    </div>
                                )}
                            </div>
                            <div className="app-pto-requests-header-title-name">
                                {getFullName(employeeRecord)}
                            </div>
                            {""}
                        </div>
                    }
                </div>
                <div className="app-pto-requests-header-actions">
                    {disabled && (
                        <Icon
                            variant="tertiary"
                            size="md"
                            icon={<RefreshCw size={16} />}
                            onClick={() => handleRefresh()}
                        />
                    )}
                </div>
            </div>
                
            <div className="app-pto-requests-content">
                {!requests && <div>Loading...</div>}
                {requests?.length === 0 && (
                    <div className="app-pto-requests-zero-state">
                        <div className="app-pto-requests-zero-state-icon">
                            <FileText size={48} />
                        </div>
                        <h3 className="app-pto-requests-zero-state-title">No Time Off Requests Found</h3>
                        <p className="app-pto-requests-zero-state-description">
                            There are no time off requests in your workspace.
                        </p>
                    </div>
                )}
                {requests && requests.length > 0 && (
                    <div className="app-pto-requests-table-container">
                        <table className="app-pto-requests-table">
                            <thead>
                                <tr>
                                    <th>Employee</th>
                                    <th>Type</th>
                                    <th>Start Date</th>
                                    <th>End Date</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {requests.map((request: TimeOffRequestExtended) => {
                                    const er = request.employeeRecord;

                                    const firstName = erGetCPValue(er, "system-firstName");
                                    const lastName = erGetCPValue(er, "system-lastName");

                                    return (
                                        <tr key={request.id} className="app-pto-requests-table-row">
                                            <td className="app-pto-requests-name-cell">
                                                <div className="app-pto-requests-employee-content">
                                                    <ValidatedImage
                                                        imageUrl={er.avatarURL || ""}
                                                        fallbackUrl={getDefaultUserAvatar(`${firstName} ${lastName}` || "")}
                                                        alt={`${firstName} ${lastName}`}
                                                        className="app-pto-requests-employee-avatar"
                                                    />
                                                    <span>{firstName} {lastName}</span>
                                                </div>
                                            </td>
                                            <td className="app-pto-requests-type-cell">
                                                {PTOPolicyTypeDescription[request.paidTimeOffPolicy.type]}
                                            </td>
                                            <td className="app-pto-requests-date-cell">
                                                {new Date(request.startDate).toLocaleDateString()}
                                            </td>
                                            <td className="app-pto-requests-date-cell">
                                                {new Date(request.endDate).toLocaleDateString()}
                                            </td>
                                            <td className="app-pto-requests-status-cell">
                                                {TimeOffRequestStatusLabels[request.status]}
                                            </td>
                                            <td className="app-pto-requests-actions-cell">
                                                <div className="app-pto-requests-actions-group">
                                                {request.status === TimeOffRequestStatus.PENDING && (
                                                    <>
                                                        <Icon
                                                            variant="primary"
                                                            size="md"
                                                            onClick={() => handleApprove(request.id)}
                                                            icon={<Check size={16} />}
                                                        />
                                                        <Icon
                                                            variant="tertiary"
                                                            size="md"
                                                            onClick={() => handleReject(request.id)}
                                                            icon={<X size={16} />}
                                                        />
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    )   
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* {disabled && (
                <div className="app-pto-requests-disabled">
                    PTO Request no longer available.
                </div>
            )} */}
        </div>
    );
};